import React, { useState, useEffect } from 'react';
import { rankAPI } from '../api';
import { useAuth } from '../context/AuthContext';

const Ranks = () => {
  const { isAuthenticated } = useAuth();
  const [ranks, setRanks] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchRanks();
      fetchUserRank();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchRanks = async () => {
    try {
      const response = await rankAPI.getAllRanks();
      setRanks(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load ranks');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRank = async () => {
    try {
      const response = await rankAPI.getMyRank();
      setUserRank(response.data);
    } catch (err) {
      console.error('Error fetching user rank:', err);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen text-gray-500">Loading ranks...</div>;
  if (error) return <div className="flex items-center justify-center min-h-screen text-red-600">{error}</div>;
  if (!isAuthenticated) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center">
        <div className="text-2xl font-bold text-red-600 mb-4">Authentication Required</div>
        <p className="text-gray-600">Please login to access this page.</p>
        <div className="mt-4">
          <button 
            onClick={() => window.location.href = '/login'}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="mb-10">
        <h1 className="text-4xl font-bold text-secondary mb-2">Rank System</h1>
        <p className="text-gray-500">Achieve higher ranks and unlock exclusive benefits</p>
      </div>

      {/* Current Rank Progress */}
      {userRank && (
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-secondary mb-6">Your Progress</h2>
          <div className="card bg-white rounded-lg shadow-md p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <div className="text-center">
                <h3 className="text-3xl font-bold text-primary mb-2">{userRank.currentRank.rankName}</h3>
                <p className="text-gray-600">Current Rank</p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-3 text-center font-semibold">
                  Business Amount: Rs. {userRank.userBusiness?.toLocaleString()}
                </p>
                
                <div className="w-full bg-gray-200 rounded-full h-4 mb-3">
                  <div 
                    className="bg-gradient-to-r from-primary to-primary-light h-4 rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min(userRank.progressPercentage, 100)}%` }}
                  ></div>
                </div>

                <p className="text-center text-sm text-gray-600 font-semibold mb-2">
                  {userRank.progressPercentage?.toFixed(1)}% towards next rank
                </p>

                {userRank.nextRank ? (
                  <p className="text-center text-sm text-primary font-bold">
                    Rs. {userRank.businessToNextRank?.toLocaleString()} needed
                  </p>
                ) : (
                  <p className="text-center text-sm text-accent font-bold">Maximum rank achieved!</p>
                )}
              </div>

              {userRank.nextRank && (
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-secondary mb-2">{userRank.nextRank.rankName}</h3>
                  <p className="text-gray-600">Next Rank</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* All Ranks Table */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-secondary mb-6">All Ranks</h2>
        <div className="card bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Rank</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Business Required</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">One-time Reward</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Benefits</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {ranks.map((rank, idx) => (
                  <tr 
                    key={idx} 
                    className={`hover:bg-gray-50 transition-colors ${userRank?.currentRank.rankName === rank.rankName ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-6 py-4 text-sm font-semibold text-secondary">
                      <span className="inline-block px-3 py-1 bg-primary/10 text-primary rounded-full">
                        {rank.rankName}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-700">Rs. {rank.businessRequired?.toLocaleString()}</td>
                    <td className="px-6 py-4 text-right text-sm font-bold text-primary">Rs. {rank.rewardAmount?.toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {rank.benefits && rank.benefits.length > 0 ? (
                        <ul className="list-disc list-inside space-y-1">
                          {rank.benefits.map((benefit, i) => (
                            <li key={i} className="text-xs text-gray-600">{benefit}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Rank Information */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-secondary mb-6">Rank Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-secondary mb-4">How to Achieve Higher Ranks</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold mt-1">✓</span>
                <span className="text-gray-700">Build your personal business through product purchases</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold mt-1">✓</span>
                <span className="text-gray-700">Sponsor direct members to create a network</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold mt-1">✓</span>
                <span className="text-gray-700">Earn commissions from both direct and indirect business</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold mt-1">✓</span>
                <span className="text-gray-700">Reach the required business threshold for each rank</span>
              </li>
            </ul>
          </div>

          <div className="card bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-secondary mb-4">Rank Benefits</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-accent font-bold mt-1">★</span>
                <span className="text-gray-700">One-time rank achievement rewards</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-accent font-bold mt-1">★</span>
                <span className="text-gray-700">Higher commission percentages</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-accent font-bold mt-1">★</span>
                <span className="text-gray-700">Exclusive member benefits</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-accent font-bold mt-1">★</span>
                <span className="text-gray-700">Priority support and recognition</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-accent font-bold mt-1">★</span>
                <span className="text-gray-700">Vehicle incentives at top ranks</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ranks;
